﻿import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Loader } from '@progress/kendo-react-indicators';
import { InputPrefix, TextBox, TextBoxHandle } from '@progress/kendo-react-inputs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { List } from 'react-window';
import { AirportSmallDto } from '../../../../../airports/airport-dto';
import Coordinate from '../../../../../common/coordinate';
import { FlightSearchResult } from '../../../../../flights/interop/flight-search-result';
import commonFormatter from '../../../../../formatters/common-formatter';
import {
    AirportTowerOutline,
    ArrowRightOutline,
    ChevronDownOutline,
    ChevronUpOutline,
    CloseOutline,
    ExternalLinkOutline,
    LightbulbOutline,
    LocationPinOutline,
    MapOutline,
    SearchOutline
} from '../../../../../icons/icons';
import { MessageType } from '../../../../../services/actor-messages';
import { FindNavdataResponseModel, NavDataLookupApiService } from '../../../../../services/axios/axios-api-generated';
import FlightStatusBadge from '../../../../components/FlightStatusBadge';
import MarkerText from '../../../../components/inputs/MarkerText';
import { EventBus } from '../../../../contexts/event-bus';
import { useWorkspaceItemContext } from '../../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { useFlightWorker } from '../../../../hooks/use-flight-worker';
import useMap from '../../../../hooks/use-map';
import { setSetting } from '../../../../state/slices/settings-slice';
import { toggleLeftFlyout } from '../../../../state/slices/workspace-slice';
import { useFormStringValue } from '../../hooks/forms';
import { FlyoutActions, useFlyoutHeader } from '../Flyout';
import PositionDetails from './PositionDetails';

const DEBOUNCE_MS = 300;
const LOADER_DELAY_MS = 150; // Only show loader if query takes longer than this
const MIN_NAVDATA_SEARCH_LENGTH = 2; // Navaids include 2-character NDB IDs
const MIN_FLIGHTS_AIRPORTS_SEARCH_LENGTH = 3; // Keep flight/airport queries scoped to avoid overly broad results

function SearchSection({
    title,
    expanded,
    onToggle,
    children
}: {
    title: string;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className={`k-expander${expanded ? ' k-expanded' : ''}`} dir="ltr">
            <div
                role="button"
                aria-expanded={expanded}
                tabIndex={0}
                className="k-expander-header"
                onClick={onToggle}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle()}>
                <div className="k-expander-title">{title}</div>
                <span className="k-spacer" />
                <span className="k-expander-indicator">
                    <SvgIcon icon={expanded ? ChevronUpOutline : ChevronDownOutline} />
                </span>
            </div>
            {expanded && children}
        </div>
    );
}

export default function SearchFlyout() {
    const map = useMap();
    const dispatch = useAppDispatch();
    const { setSubMenu } = useFlyoutHeader();
    const context = useWorkspaceItemContext();
    const airportDisplayStyle = useAppSelector((x) => x.settings.airportIdDisplayStyle);
    const commandCenterHintDismissed = useAppSelector((x) => x.settings.commandCenterHintDismissed);

    const search = useFormStringValue('');
    const [isLoadingFlights, setIsLoadingFlights] = useState<boolean>(false);
    const [isLoadingAirports, setIsLoadingAirports] = useState<boolean>(false);
    const [isLoadingNavdata, setIsLoadingNavdata] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [flightResults, setFlightResults] = useState<FlightSearchResult[]>([]);
    const [airportResults, setAirportResults] = useState<AirportSmallDto[]>([]);
    const [navdataResults, setNavdataResults] = useState<FindNavdataResponseModel | null>(null);
    const [expandedFlights, setExpandedFlights] = useState<boolean>(true);
    const [expandedAirports, setExpandedAirports] = useState<boolean>(true);
    const [expandedNavaids, setExpandedNavaids] = useState<boolean>(true);
    const [expandedFixes, setExpandedFixes] = useState<boolean>(true);
    const [expandedAirways, setExpandedAirways] = useState<boolean>(true);
    const [lastKnownLocationItem, setLastKnownLocationItem] = useState<string | null>(null);
    const [hasExactFlightResult, setHasExactFlightResult] = useState<boolean>(false);
    const worker = useFlightWorker();

    const reqIdRef = useRef(0);
    const timeoutRef = useRef<number | null>(null);
    const activeReqRef = useRef<number | null>(null);
    const loaderTimeoutRef = useRef<number | null>(null);
    const searchInputRef = useRef<TextBoxHandle>(null);

    const runQuery = useCallback(
        async (term: string, requestId: number) => {
            activeReqRef.current = requestId;

            // Flights/airports queries are only worthwhile once the term is long enough
            // to keep result sets small (avoids scanning on very short terms).
            const includeFlightsAndAirports = term.length >= MIN_FLIGHTS_AIRPORTS_SEARCH_LENGTH;

            setIsLoadingFlights(includeFlightsAndAirports);
            setIsLoadingAirports(includeFlightsAndAirports);
            setIsLoadingNavdata(true);

            loaderTimeoutRef.current = self.setTimeout(() => {
                setShowLoader(true);
            }, LOADER_DELAY_MS);

            if (includeFlightsAndAirports) {
                // Query flights
                worker.actor
                    .sendAsync({
                        type: MessageType.queryFlightsSearch,
                        data: term
                    })
                    .then((results) => {
                        if (activeReqRef.current === requestId) {
                            setHasExactFlightResult(results.some((f) => f.acid.toLowerCase() === term.toLowerCase()));
                            setFlightResults(results);
                            setIsLoadingFlights(false);
                        }
                    })
                    .catch(() => {
                        if (activeReqRef.current === requestId) {
                            setIsLoadingFlights(false);
                        }
                    });

                // Query airports
                worker.actor
                    .sendAsync({
                        type: MessageType.queryAirportSearch,
                        data: term
                    })
                    .then((results) => {
                        if (activeReqRef.current === requestId) {
                            setAirportResults(results);
                            setIsLoadingAirports(false);
                        }
                    })
                    .catch(() => {
                        if (activeReqRef.current === requestId) {
                            setIsLoadingAirports(false);
                        }
                    });
            } else {
                setFlightResults([]);
                setAirportResults([]);
            }

            //Query navdata (includes Navaids, which can be as short as 2-character NDB IDs)
            NavDataLookupApiService.findNavData(
                {
                    body: { searchText: term }
                },
                { withAuthorization: true }
            )
                .then((results) => {
                    if (activeReqRef.current === requestId) {
                        setNavdataResults(results ?? null);
                        setIsLoadingNavdata(false);
                    }
                })
                .catch(() => {
                    if (activeReqRef.current === requestId) {
                        setIsLoadingNavdata(false);
                        setNavdataResults(null);
                    }
                });
        },
        [worker]
    );

    useEffect(() => {
        if (!isLoadingFlights && !isLoadingAirports && !isLoadingNavdata) {
            if (loaderTimeoutRef.current) {
                self.clearTimeout(loaderTimeoutRef.current);
                loaderTimeoutRef.current = null;
            }
            setShowLoader(false);
        }
    }, [isLoadingFlights, isLoadingAirports, isLoadingNavdata]);

    useEffect(() => {
        if (lastKnownLocationItem) {
            setSubMenu({
                title: `${lastKnownLocationItem} - Current & last known location`,
                onBack: () => setLastKnownLocationItem(null)
            });
        }
    }, [lastKnownLocationItem, setSubMenu]);

    useEffect(() => {
        const term = search.value.trim();

        if (term.length < MIN_NAVDATA_SEARCH_LENGTH) {
            if (timeoutRef.current) {
                self.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (loaderTimeoutRef.current) {
                self.clearTimeout(loaderTimeoutRef.current);
                loaderTimeoutRef.current = null;
            }
            setFlightResults([]);
            setAirportResults([]);
            setNavdataResults(null);
            setIsLoadingFlights(false);
            setIsLoadingAirports(false);
            setIsLoadingNavdata(false);
            setShowLoader(false);
            return;
        }

        if (timeoutRef.current) {
            self.clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = self.setTimeout(() => {
            const id = ++reqIdRef.current;
            runQuery(term, id);
        }, DEBOUNCE_MS);

        return () => {
            if (timeoutRef.current) {
                self.clearTimeout(timeoutRef.current);
            }
            if (loaderTimeoutRef.current) {
                self.clearTimeout(loaderTimeoutRef.current);
            }
        };
    }, [search.value, runQuery]);

    useEffect(() => {
        const timer = self.setTimeout(() => {
            searchInputRef.current?.element?.focus();
        }, 300); // Delay to allow flyout to open

        return () => self.clearTimeout(timer);
    }, []);

    const handleCreateFilter = React.useCallback((acid: string) => {
        EventBus.fire('add-new-filter', {
            template: {
                name: acid,
                acid: acid
            }
        });
    }, []);

    const onOpenAirportSummary = useCallback(
        (airportId: string) => {
            dispatch(toggleLeftFlyout({ type: 'airport', airportId }));
        },
        [dispatch]
    );

    const showFlightsSection = !isLoadingFlights && flightResults.length > 0;
    const showAirportsSection = !isLoadingAirports && airportResults.length > 0;
    const showFixesSection = !isLoadingNavdata && (navdataResults?.fixes ?? []).length > 0;
    const showNavaidsSection = !isLoadingNavdata && (navdataResults?.navaids ?? []).length > 0;
    const showAirwaysSection = !isLoadingNavdata && (navdataResults?.airways ?? []).length > 0;

    return (
        <div className="d-flex flex-column w-100" style={{ minHeight: 0, overflowY: 'auto', minWidth: '300px' }}>
            {lastKnownLocationItem ? (
                <>
                    <FlyoutActions></FlyoutActions>
                    <PositionDetails
                        acid={lastKnownLocationItem}
                        onGoToCoordinates={(coords) => map?.goToCoordinates(coords)}
                        onOpenAirportDetails={(id) => onOpenAirportSummary?.(id)}
                    />
                </>
            ) : (
                <>
                    <FlyoutActions>
                        <TextBox
                            prefix={() => (
                                <InputPrefix>
                                    <SvgIcon icon={SearchOutline} />
                                </InputPrefix>
                            )}
                            rounded={'large'}
                            style={{ width: '240px' }}
                            className={'input-uppercase'}
                            placeholder={'Search'}
                            data-allow-shortcuts="true"
                            {...search}
                            ref={searchInputRef}
                        />
                    </FlyoutActions>
                    {search.value.trim().length >= 3 && !hasExactFlightResult && (
                        <div className={'pt-1 px-2 d-flex w-100 justify-content-between align-items-center'}>
                            <div>
                                <div className={'overflow-hidden text-truncate'} style={{ maxWidth: '240px' }}>
                                    {search.value.toUpperCase()}
                                </div>
                                <div className={'secondary-text d-flex align-items-center gap-1'}>Search for last known location</div>
                            </div>
                            <div>
                                <Button
                                    className={'button-small'}
                                    fillMode={'flat'}
                                    svgIcon={LocationPinOutline}
                                    title={'Current & last known location details'}
                                    onClick={() => setLastKnownLocationItem(search.value.toUpperCase())}
                                />
                                <Button
                                    className={'button-small'}
                                    fillMode={'flat'}
                                    svgIcon={ExternalLinkOutline}
                                    title={'Create filter'}
                                    onClick={() => handleCreateFilter(search.value.toUpperCase())}
                                />
                            </div>
                        </div>
                    )}
                    {showLoader && <Loader type={'infinite-spinner'} className="mx-auto pt-2" />}
                    {showFlightsSection && (
                        <SearchSection
                            title={`Flights (${flightResults.length})`}
                            expanded={expandedFlights}
                            onToggle={() => setExpandedFlights(!expandedFlights)}>
                            <div style={{ maxHeight: '412px', overflowY: 'auto' }}>
                                <List
                                    defaultHeight={290}
                                    style={{ maxHeight: '412px', overflowY: 'auto' }}
                                    rowHeight={46}
                                    rowCount={flightResults.length}
                                    rowComponent={({ index, style, flightResults }) => {
                                        const flight = flightResults[index];
                                        return (
                                            <div style={style} className="border-top">
                                                <div className={'d-flex pt-1 px-2 d-flex w-100 justify-content-between align-items-center'}>
                                                    <div>
                                                        <div className={'d-flex align-items-center gap-1'}>
                                                            <MarkerText highlight={search.value}>{flight.acid}</MarkerText>
                                                            <FlightStatusBadge
                                                                style={{
                                                                    alignSelf: 'center',
                                                                    display: 'inline-flex',
                                                                    lineHeight: 1.3,
                                                                    flex: '0 0 auto'
                                                                }}
                                                                flightStatus={flight.flightStatus}
                                                                ground={flight.ground}
                                                            />
                                                        </div>
                                                        <div className={'secondary-text d-flex align-items-center gap-1'}>
                                                            <span>
                                                                {commonFormatter.formatAirportId(
                                                                    flight.originAirport,
                                                                    airportDisplayStyle
                                                                ) ??
                                                                    flight.originFallback ??
                                                                    'N/A'}
                                                            </span>
                                                            <SvgIcon icon={ArrowRightOutline} />
                                                            <span>
                                                                {commonFormatter.formatAirportId(
                                                                    flight.destinationAirport,
                                                                    airportDisplayStyle
                                                                ) ??
                                                                    flight.destinationFallback ??
                                                                    'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Button
                                                            className={'button-small'}
                                                            fillMode={'flat'}
                                                            svgIcon={LocationPinOutline}
                                                            title={'Current & last known location details'}
                                                            onClick={() => setLastKnownLocationItem(flight.acid)}
                                                        />
                                                        <Button
                                                            className={'button-small'}
                                                            fillMode={'flat'}
                                                            svgIcon={ExternalLinkOutline}
                                                            title={'Create filter'}
                                                            onClick={() => handleCreateFilter(flight.acid)}
                                                        />
                                                        <Button
                                                            className={'button-small'}
                                                            fillMode={'flat'}
                                                            svgIcon={MapOutline}
                                                            title={'Show on map'}
                                                            onClick={() => {
                                                                context.flightLayerFacade.selection.selectFlight(flight.id, {
                                                                    source: 'search'
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }}
                                    rowProps={{ flightResults }}></List>
                            </div>
                        </SearchSection>
                    )}
                    {showAirportsSection && (
                        <SearchSection
                            title={`Airports (${airportResults.length})`}
                            expanded={expandedAirports}
                            onToggle={() => setExpandedAirports(!expandedAirports)}>
                            <div style={{ maxHeight: '412px', overflowY: 'auto' }}>
                                {airportResults.map((result, index) => (
                                    <div key={index} className="border-top">
                                        <div key={index} className={'pt-1 px-2 d-flex w-100 justify-content-between align-items-center'}>
                                            <div>
                                                <MarkerText highlight={search.value}>
                                                    {[result.iata || result.faa || '', result.icao || '']
                                                        .filter((s) => s.trim() !== '')
                                                        .join(' / ')}
                                                </MarkerText>
                                                <div
                                                    className={'secondary-text text-truncate d-block'}
                                                    title={result.name ?? undefined}
                                                    style={{ maxWidth: '200px' }}>
                                                    <MarkerText highlight={search.value}>{result.name ?? 'N/A'}</MarkerText>
                                                </div>
                                            </div>
                                            <div>
                                                <Button
                                                    className={'button-small'}
                                                    fillMode={'flat'}
                                                    svgIcon={AirportTowerOutline}
                                                    title={'Airport Summary'}
                                                    onClick={() => onOpenAirportSummary?.(result.iata || result.faa || result.icao || '')}
                                                />
                                                <Button
                                                    className={'button-small'}
                                                    fillMode={'flat'}
                                                    svgIcon={MapOutline}
                                                    title={'Show on map'}
                                                    onClick={() => {
                                                        if (result.position.isValid) {
                                                            map?.goToCoordinates(result.position);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </SearchSection>
                    )}
                    {showNavaidsSection && (
                        <SearchSection
                            title={`Navaids (${(navdataResults?.navaids ?? []).length})`}
                            expanded={expandedNavaids}
                            onToggle={() => setExpandedNavaids(!expandedNavaids)}>
                            <div style={{ maxHeight: '412px', overflowY: 'auto' }}>
                                {(navdataResults?.navaids ?? []).map((result, index) => (
                                    <div key={index} className="border-top">
                                        <div key={index} className={'pt-1 px-2 d-flex w-100 justify-content-between align-items-center'}>
                                            <div>
                                                <MarkerText highlight={search.value}>{result.id ?? 'N/A'}</MarkerText>
                                                <div className={'secondary-text d-flex align-items-center gap-1'}>
                                                    <MarkerText highlight={search.value}>{result.name ?? 'N/A'}</MarkerText>
                                                </div>
                                            </div>
                                            <div>
                                                <Button
                                                    className={'button-small'}
                                                    fillMode={'flat'}
                                                    svgIcon={MapOutline}
                                                    title={'Show on map'}
                                                    onClick={() => {
                                                        if (result.lat && result.lon && result.lon < 200 && result.lat < 200) {
                                                            map?.goToCoordinates(new Coordinate(result.lat, result.lon));
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </SearchSection>
                    )}
                    {showFixesSection && (
                        <SearchSection
                            title={`Fixes (${(navdataResults?.fixes ?? []).length})`}
                            expanded={expandedFixes}
                            onToggle={() => setExpandedFixes(!expandedFixes)}>
                            <div style={{ maxHeight: '412px', overflowY: 'auto' }}>
                                {(navdataResults?.fixes ?? []).map((result, index) => (
                                    <div key={index} className="border-top">
                                        <div key={index} className={'pt-1 px-2 d-flex w-100 justify-content-between align-items-center'}>
                                            <div>
                                                <MarkerText highlight={search.value}>{result.id ?? 'N/A'}</MarkerText>
                                                <div className={'secondary-text d-flex align-items-center gap-1'}>
                                                    <MarkerText highlight={search.value}>{result.name ?? 'N/A'}</MarkerText>
                                                </div>
                                            </div>
                                            <div>
                                                <Button
                                                    className={'button-small'}
                                                    fillMode={'flat'}
                                                    svgIcon={MapOutline}
                                                    title={'Show on map'}
                                                    onClick={() => {
                                                        if (result.lat && result.lon && result.lon < 200 && result.lat < 200) {
                                                            map?.goToCoordinates(new Coordinate(result.lat, result.lon));
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </SearchSection>
                    )}
                    {showAirwaysSection && (
                        <SearchSection
                            title={`Airways (${(navdataResults?.airways ?? []).length})`}
                            expanded={expandedAirways}
                            onToggle={() => setExpandedAirways(!expandedAirways)}>
                            <div style={{ maxHeight: '412px', overflowY: 'auto' }}>
                                {(navdataResults?.airways ?? []).map((result, index) => (
                                    <div key={index} className="border-top">
                                        <div key={index} className={'pt-1 px-2 d-flex w-100 justify-content-between align-items-center'}>
                                            <div>
                                                <MarkerText highlight={search.value}>{result.id ?? 'N/A'}</MarkerText>
                                                <div className={'secondary-text d-flex align-items-center gap-1'}>
                                                    <MarkerText highlight={search.value}>{result.name ?? 'N/A'}</MarkerText>
                                                </div>
                                            </div>
                                            <div>
                                                <Button
                                                    className={'button-small'}
                                                    fillMode={'flat'}
                                                    svgIcon={MapOutline}
                                                    title={'Show on map'}
                                                    onClick={() => {
                                                        if (result.lat && result.lon && result.lon < 200 && result.lat < 200) {
                                                            map?.goToCoordinates(new Coordinate(result.lat, result.lon));
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </SearchSection>
                    )}
                    {!commandCenterHintDismissed && (
                        <div className="border-top mt-1 px-2 py-2 d-flex align-items-center gap-2 secondary-text" style={{ maxWidth: 300 }}>
                            <SvgIcon icon={LightbulbOutline} className="flex-shrink-0" />
                            <div
                                className="clickable flex-grow-1"
                                style={{ minWidth: 0, fontSize: '0.8rem', lineHeight: 1.2 }}
                                title="Open Command Center"
                                onClick={() => EventBus.fire('open-command-center', null)}>
                                <span>Tip: press </span>
                                <span className="fe-badge-outline">Ctrl+K</span>
                                <span> to search shortcuts, layers &amp; more.</span>
                            </div>
                            <Button
                                className="button-small flex-shrink-0"
                                fillMode="flat"
                                svgIcon={CloseOutline}
                                title="Dismiss"
                                onClick={() => dispatch(setSetting('commandCenterHintDismissed', true))}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
